import React, { useCallback, useEffect, useRef, useState } from "react";
import { askSpeakingHelper, saveWordBankSentence } from "../api";

/**
 * Speaking Helper — a quick, always-available aid for LIVE Arabic conversations
 * (the in-app practice call, or an external Zoom / Google Meet running alongside).
 *
 * The user types, in English, what they want to say or how they want to reply,
 * and instantly gets up to 3 natural fuṣḥā (MSA) ways to say it — Arabic with full
 * harakat plus an English translation — ready to read aloud. Nothing is saved.
 *
 * Built for speed: autofocus, Enter to submit, Esc to close, one-tap copy.
 */
export default function SpeakingHelper({ onClose }) {
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(false);
  const [replies, setReplies] = useState([]);
  const [error, setError] = useState("");
  const [remaining, setRemaining] = useState(null);
  const [copiedIdx, setCopiedIdx] = useState(null);
  const [savedIdx, setSavedIdx] = useState(() => new Set());
  const [savingIdx, setSavingIdx] = useState(null);
  const inputRef = useRef(null);

  // Autofocus the input the moment the helper opens.
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Esc closes the helper from anywhere within it.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const submit = useCallback(async () => {
    const trimmed = text.trim();
    if (!trimmed || loading) return;
    setLoading(true);
    setError("");
    try {
      const data = await askSpeakingHelper(trimmed);
      setReplies(data.replies || []);
      setRemaining(data.asks_remaining ?? null);
      setSavedIdx(new Set()); // fresh results — clear per-card saved markers
      setCopiedIdx(null);
      if (!data.replies || data.replies.length === 0) {
        setError("No suggestions came back — try rephrasing.");
      }
    } catch (err) {
      setError(err.message || "Something went wrong. Please try again.");
      // On a hard limit (429) clear stale results so the message is unambiguous.
      if (err.status === 429) setReplies([]);
    } finally {
      setLoading(false);
    }
  }, [text, loading]);

  const onInputKeyDown = (e) => {
    // Enter submits; Shift+Enter inserts a newline.
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      submit();
    }
  };

  const copy = async (arabic, idx) => {
    try {
      await navigator.clipboard.writeText(arabic);
      setCopiedIdx(idx);
      setTimeout(() => setCopiedIdx((c) => (c === idx ? null : c)), 1500);
    } catch {
      /* clipboard unavailable — non-fatal */
    }
  };

  const save = async (reply, idx) => {
    if (savedIdx.has(idx) || savingIdx === idx) return;
    setSavingIdx(idx);
    setError("");
    try {
      await saveWordBankSentence({ arabic: reply.arabic, translation: reply.translation || "" });
      setSavedIdx((prev) => new Set(prev).add(idx));
    } catch (err) {
      setError(err.message || "Couldn't save to Word Bank.");
    } finally {
      setSavingIdx((s) => (s === idx ? null : s));
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-start sm:items-center justify-center p-3 sm:p-4 bg-black/50 backdrop-blur-sm">
      <div className="surface-glass rounded-2xl shadow-2xl w-full max-w-lg max-h-[92vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between gap-3 px-5 py-3.5 shrink-0" style={{ borderBottom: "1px solid var(--panel-line)" }}>
          <div className="flex items-center gap-2.5">
            <span className="text-xl select-none">🗣️</span>
            <div>
              <h2 className="text-base font-semibold text-dt-parchment leading-tight">Speaking Helper</h2>
              <p className="text-xs text-dt-parchment-dim leading-tight">Say it in fuṣḥā — for your live conversation</p>
            </div>
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            className="shrink-0 text-dt-parchment-dim hover:text-dt-parchment text-xl leading-none p-1"
          >
            ✕
          </button>
        </div>

        {/* Input */}
        <div className="px-5 pt-4 shrink-0">
          <textarea
            ref={inputRef}
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={onInputKeyDown}
            rows={2}
            placeholder="Type in English what you want to say…"
            className="dt-field w-full resize-none px-3.5 py-2.5 rounded-xl text-base"
          />
          <div className="flex items-center justify-between gap-3 mt-2.5">
            <span className="text-xs text-dt-parchment-dim">
              {remaining != null ? `${remaining} left today` : "Press Enter to get suggestions"}
            </span>
            <button
              onClick={submit}
              disabled={loading || !text.trim()}
              className="px-4 py-2 rounded-xl bg-dt-gold text-dt-ink hover:brightness-105 disabled:opacity-40 disabled:cursor-not-allowed text-sm font-semibold transition-[filter]"
            >
              {loading ? "Thinking…" : "Get Arabic"}
            </button>
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="mx-5 mt-3 px-3.5 py-2.5 rounded-xl text-sm shrink-0"
            style={{ background: "var(--tag-rose-bg)", border: "1px solid var(--panel-line)", color: "var(--tag-rose)" }}>
            {error}
          </div>
        )}

        {/* Results */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
          {replies.map((r, idx) => (
            <div
              key={idx}
              className="surface-glass surface--interactive rounded-2xl px-4 py-3.5"
            >
              <div className="flex items-start justify-between gap-3">
                <p dir="rtl" lang="ar" className="font-arabic text-2xl leading-loose text-dt-parchment flex-1">
                  {r.arabic}
                </p>
                <div className="shrink-0 mt-1 flex flex-col items-end gap-1">
                  <button
                    onClick={() => copy(r.arabic, idx)}
                    title="Copy Arabic"
                    className="text-xs px-2 py-1 rounded-lg text-dt-parchment-dim hover:text-dt-gold-bright transition-colors"
                  >
                    {copiedIdx === idx ? "Copied ✓" : "Copy"}
                  </button>
                  <button
                    onClick={() => save(r, idx)}
                    disabled={savedIdx.has(idx) || savingIdx === idx}
                    title="Save to Word Bank"
                    className="text-xs px-2 py-1 rounded-lg text-dt-parchment-dim hover:text-dt-gold-bright disabled:opacity-60 transition-colors"
                  >
                    {savedIdx.has(idx) ? "Saved ✓" : savingIdx === idx ? "Saving…" : "Save"}
                  </button>
                </div>
              </div>
              {r.translation && (
                <p className="text-sm text-dt-parchment-dim mt-1.5">{r.translation}</p>
              )}
            </div>
          ))}

          {!loading && replies.length === 0 && !error && (
            <div className="text-center text-sm text-dt-parchment-dim py-8">
              Suggestions will appear here.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
